import React, { useState } from 'react';
import { Heart, X, Facebook, Mail, MapPin, Phone } from 'lucide-react';
import { trackEvent, trackCustomEvent, trackClarityEvent } from './Analytics';
import BugReportModal from './BugReportModal';
import { showSuccess, showError } from '../utils/toast';

const MarketingFooter = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const footerSections = [
    {
      title: 'Product',
      links: [
        { name: 'Features', onClick: () => { trackEvent('click', 'navigation', 'footer_features'); onNavigate('features'); } },
        { name: 'Solutions', onClick: () => { trackEvent('click', 'navigation', 'footer_features'); onNavigate('features'); } },
        { name: 'Pricing', onClick: () => { trackEvent('click', 'navigation', 'footer_pricing'); onNavigate('pricing'); } },
        { name: 'Dashboard', onClick: () => { trackEvent('click', 'navigation', 'footer_dashboard'); onNavigate('app'); } },
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Blog', onClick: () => { trackEvent('click', 'navigation', 'footer_blog'); onNavigate('blog'); } },
        { name: 'Help Center', onClick: () => { trackEvent('click', 'support', 'footer_help_center'); onNavigate('help'); } },
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', onClick: () => { onNavigate && onNavigate('about'); } },
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', onClick: () => { onNavigate && onNavigate('privacy'); } },
        { name: 'Terms of Service', onClick: () => { onNavigate && onNavigate('terms'); } },
        { name: 'Cookie Policy', onClick: () => { onNavigate && onNavigate('cookie-policy'); } },
        { name: 'GDPR Compliance', onClick: () => { onNavigate && onNavigate('gdpr'); } },
      ]
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    trackEvent('form_start', 'contact', 'footer_contact_form');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        trackCustomEvent('contact_form_success', { subject: contactForm.subject, source: 'footer' });
        showSuccess('Thank you! We\'ll get back to you soon.');
        setContactForm({ name: '', email: '', subject: '', message: '' });
        setShowContactModal(false);
      } else {
        trackEvent('form_error', 'contact', 'submission_failed', response.status);
        showError('Sorry, something went wrong. Please try again.');
      }
    } catch (error) {
      trackEvent('form_error', 'contact', 'network_error');
      showError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-neutral-900 pt-20 pb-10 overflow-hidden relative">
      {/* Decorative Blur */}
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-primary/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-20">
          {/* Brand Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <span className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="text-[#2E75B6]">Subscription Manager</span>
                <span className="text-[#E8852E]">.pro</span>
              </span>
            </div>
            <p className="text-neutral-400 text-lg leading-relaxed max-w-sm">
              The smartest way to manage your subscriptions. Save money, stay organized, and never miss a renewal again.
            </p>
          </div>

          {/* Links Grid */}
          {footerSections.map((section, index) => (
            <div key={index} className="col-span-1">
              <h3 className="text-sm font-bold text-neutral-400 uppercase tracking-widest mb-6">{section.title}</h3>
              <ul className="space-y-4">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.onClick ? (
                      <button
                        onClick={link.onClick}
                        className="text-neutral-300 hover:text-primary-400 transition-colors font-semibold"
                      >
                        {link.name}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        className="text-neutral-300 hover:text-primary-400 transition-colors font-semibold"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-neutral-800 flex flex-col lg:flex-row justify-between items-center gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <p className="text-neutral-500 text-sm font-semibold">
              © {currentYear} SubscriptionManager. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => setShowBugReportModal(true)}
                className="text-neutral-500 hover:text-primary-400 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Report a Bug
              </button>
              <button
                onClick={() => setShowContactModal(true)}
                className="text-neutral-500 hover:text-primary-400 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>

          <div className="flex items-center text-neutral-400 text-sm font-semibold bg-white/10 px-4 py-2 rounded-pill border border-neutral-700">
            Made with <Heart size={16} className="mx-2 text-error animate-pulse" fill="#EF4444" /> for humans
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-neutral-0 rounded-card p-8 max-w-lg w-full shadow-strong animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-black text-neutral-900 mb-1">Get in touch</h2>
                <p className="text-neutral-500 font-semibold">We'd love to hear from you.</p>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="p-2 text-neutral-400 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-neutral-700 ml-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-5 py-3 rounded-xl border border-neutral-200 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all bg-neutral-50/50"
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-neutral-700 ml-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-5 py-3 rounded-xl border border-neutral-200 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all bg-neutral-50/50"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-bold text-neutral-700 ml-1">Subject</label>
                <input
                  type="text"
                  required
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full px-5 py-3 rounded-xl border border-neutral-200 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all bg-neutral-50/50"
                  placeholder="How can we help?"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-bold text-neutral-700 ml-1">Message</label>
                <textarea
                  required
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full px-5 py-3 rounded-xl border border-neutral-200 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all bg-neutral-50/50 resize-none"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-primary-600 text-neutral-0 font-bold rounded-xl shadow-soft hover:bg-primary-700 disabled:bg-neutral-300 transition-all transform active:scale-95"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      )}

      <BugReportModal
        isOpen={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
        user={null}
      />
    </footer>
  );
};

export default MarketingFooter;
